import { useState, useRef, useEffect, useCallback } from "react";

const CHECKLIST_INTERVAL_MS = 3000;

interface UseProcessingChecklistOptions {
  itemCount: number;
  isTerminal: boolean;
  failed?: boolean;
  dataUpdatedAt?: number;
  onComplete: () => void;
  onFailed?: () => void;
}

export function useProcessingChecklist({
  itemCount,
  isTerminal,
  failed = false,
  dataUpdatedAt,
  onComplete,
  onFailed,
}: UseProcessingChecklistOptions) {
  const minAnimationMs = CHECKLIST_INTERVAL_MS * itemCount + 1500;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onFailedRef = useRef(onFailed);
  onFailedRef.current = onFailed;
  const animationDone = useRef(false);
  const pollDone = useRef(false);
  const failedRef = useRef(false);
  const seenPendingRef = useRef(false);
  const firstDataUpdatedAtRef = useRef(dataUpdatedAt);

  const tryFinish = useCallback(() => {
    if (animationDone.current && pollDone.current) {
      setActiveIndex(itemCount);
      setIsDone(true);
    }
  }, [itemCount]);

  useEffect(() => {
    if (!isTerminal) {
      seenPendingRef.current = true;
      return;
    }
    const isFreshPoll =
      dataUpdatedAt !== undefined && dataUpdatedAt !== firstDataUpdatedAtRef.current;
    if (!seenPendingRef.current && !isFreshPoll) return;
    if (failed) {
      failedRef.current = true;
    }
    pollDone.current = true;
    tryFinish();
  }, [isTerminal, failed, tryFinish, dataUpdatedAt]);

  useEffect(() => {
    const timer = setTimeout(() => {
      animationDone.current = true;
      tryFinish();
    }, minAnimationMs);
    return () => clearTimeout(timer);
  }, [tryFinish, minAnimationMs]);

  useEffect(() => {
    if (isDone) {
      const cb = failedRef.current ? onFailedRef.current : onCompleteRef.current;
      const timer = setTimeout(() => cb?.(), 500);
      return () => clearTimeout(timer);
    }
  }, [isDone]);

  useEffect(() => {
    if (isDone) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev >= itemCount - 1 ? prev : prev + 1));
    }, CHECKLIST_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isDone, itemCount]);

  return { activeIndex, isDone };
}
